import type { FC, FormEvent, ReactNode } from 'react'
import type { Gender, Patient, PatientInput } from '@/lib/clinic/types'

import { useMemo, useRef, useState } from 'react'
import { CircleAlert, Clock3, IdCard, Stethoscope, UserRound } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useClinic } from '@/hooks/use-clinic'
import { formatPatientCode } from '@/lib/clinic/ids'
import { formatDateTime, getAgeFromDob } from '@/lib/clinic/datetime'
import { appendToText, formatAge, formatPhone, phoneDigits } from '@/lib/clinic/format'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { ALLERGY_SUGGESTIONS, MEDICAL_HISTORY_SUGGESTIONS } from '@/constants/clinic'
import Field from '@/components/common/field'
import DateField from '@/components/common/date-field'
import PatientAvatar from '@/components/common/patient-avatar'
import SuggestionChips from '@/components/common/suggestion-chips'
import SegmentedControl from '@/components/common/segmented-control'

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle
} from '@/components/ui/dialog'

export type PatientSaveIntent = 'save' | 'consult'

interface PatientFormDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    patient: Patient | null
    initialName?: string
    initialPhone?: string
    onSaved: (patient: Patient, intent: PatientSaveIntent) => void
    onOpenExisting: (patient: Patient, intent: 'profile' | 'consult') => void
}

interface FormState {
    name: string
    phone: string
    ageMode: 'age' | 'dob'
    age: string
    dateOfBirth: string | null
    gender: Gender
    place: string
    address: string
    medicalHistory: string
    allergies: string
    notes: string
}

type FormErrors = Partial<Record<'name' | 'phone' | 'age' | 'dateOfBirth', string>>

const GENDER_OPTIONS: Array<{ value: Gender, label: string }> = [
    { value: 'female', label: 'Female' },
    { value: 'male', label: 'Male' },
    { value: 'other', label: 'Other' }
]

const AGE_MODE_OPTIONS: Array<{ value: 'age' | 'dob', label: string }> = [
    { value: 'age', label: 'Age' },
    { value: 'dob', label: 'Date of birth' }
]

const createInitialState = (patient: Patient | null, initialName = '', initialPhone = ''): FormState => ({
    name: patient?.name ?? initialName,
    phone: patient?.phone ?? initialPhone,
    ageMode: patient?.dateOfBirth ? 'dob' : 'age',
    age: patient?.age !== null && patient?.age !== undefined ? String(patient.age) : '',
    dateOfBirth: patient?.dateOfBirth ?? null,
    gender: patient?.gender ?? '',
    place: patient?.place ?? '',
    address: patient?.address ?? '',
    medicalHistory: patient?.medicalHistory ?? '',
    allergies: patient?.allergies ?? '',
    notes: patient?.notes ?? ''
})

const validate = (state: FormState, today: string): FormErrors => {
    const errors: FormErrors = {}
    const digits = phoneDigits(state.phone)

    if (state.name.trim().length < 2) errors.name = "Enter the patient's full name."
    if (!digits) errors.phone = 'Enter a phone number.'
    else if (digits.length < 10 || digits.length > 15) errors.phone = 'Phone number should have 10 to 15 digits.'

    if (state.ageMode === 'age' && state.age.trim()) {
        const age = Number(state.age)
        if (!Number.isInteger(age) || age < 0 || age > 120) errors.age = 'Enter an age between 0 and 120.'
    }

    if (state.ageMode === 'dob' && state.dateOfBirth && state.dateOfBirth > today) {
        errors.dateOfBirth = 'Date of birth cannot be in the future.'
    }

    return errors
}

const toInput = (state: FormState): PatientInput => ({
    name: state.name,
    phone: state.phone,
    age: state.ageMode === 'age' && state.age.trim() ? Number(state.age) : null,
    dateOfBirth: state.ageMode === 'dob' ? state.dateOfBirth : null,
    gender: state.gender,
    place: state.place,
    address: state.address,
    medicalHistory: state.medicalHistory,
    allergies: state.allergies,
    notes: state.notes
})

interface PatientFormProps extends Omit<PatientFormDialogProps, 'open'> {}

const PatientForm: FC<PatientFormProps> = ({
    onOpenChange,
    patient,
    initialName,
    initialPhone,
    onSaved,
    onOpenExisting
}): ReactNode => {
    const { data, actions, today } = useClinic()
    const [state, setState] = useState<FormState>(() => createInitialState(patient, initialName, initialPhone))
    const [errors, setErrors] = useState<FormErrors>({})
    const [duplicateAcknowledged, setDuplicateAcknowledged] = useState(false)
    const [openedAt] = useState(() => new Date())
    const savingRef = useRef(false)

    const editing = Boolean(patient)
    const nextCode = formatPatientCode(data.counters.patient + 1)
    const currentYear = openedAt.getFullYear()

    const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
        setState(current => ({ ...current, [key]: value }))
        if (key in errors) setErrors(current => ({ ...current, [key]: undefined }))
        if (key === 'phone') setDuplicateAcknowledged(false)
    }

    const matches = useMemo(() => {
        const digits = phoneDigits(state.phone)
        const lastTen = digits.slice(-10)
        const name = state.name.trim().toLowerCase()

        return data.patients
            .filter(item => item.id !== patient?.id)
            .map(item => {
                const samePhone = lastTen.length === 10 && phoneDigits(item.phone).slice(-10) === lastTen
                const sameName = name.length >= 3 && item.name.toLowerCase() === name

                return { patient: item, samePhone, sameName }
            })
            .filter(item => item.samePhone || item.sameName)
            .slice(0, 3)
    }, [data.patients, patient?.id, state.name, state.phone])

    const phoneMatch = matches.some(item => item.samePhone)
    const dobAge = state.ageMode === 'dob' && state.dateOfBirth ? getAgeFromDob(state.dateOfBirth, today) : null

    const submit = (intent: PatientSaveIntent) => {
        const nextErrors = validate(state, today)
        setErrors(nextErrors)
        if (Object.values(nextErrors).some(Boolean)) return

        if (!editing && phoneMatch && !duplicateAcknowledged) {
            setDuplicateAcknowledged(true)
            return
        }

        if (savingRef.current) return
        savingRef.current = true

        const input = toInput(state)
        const saved = patient ? actions.updatePatient(patient.id, input) : actions.registerPatient(input)

        if (saved) onSaved(saved, intent)
    }

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        submit('save')
    }

    return (
        <>
            <div className='flex items-start gap-3 border-b px-5 py-4 pr-12'>
                <span className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground'>
                    <UserRound className='size-5' />
                </span>

                <div className='flex min-w-0 flex-col gap-1'>
                    <DialogTitle className='text-lg font-semibold'>
                        {editing ? 'Edit patient details' : 'Register new patient'}
                    </DialogTitle>

                    <DialogDescription>
                        {editing ? (
                            <>
                                {patient?.code} · Registered {formatDateTime(patient?.createdAt)}
                            </>
                        ) : (
                            <>Only the name and phone number are required.</>
                        )}
                    </DialogDescription>
                </div>
            </div>

            <form id='patient-form' noValidate onSubmit={handleSubmit} className='flex-1 overflow-y-auto px-5 py-5'>
                {!editing && (
                    <div className='mb-5 grid gap-2 rounded-xl border bg-brand-soft p-3 text-[13px] sm:grid-cols-2'>
                        <span className='flex items-center gap-2 text-foreground'>
                            <IdCard className='size-4 text-primary' />
                            Patient ID
                            <span className='font-semibold tabular-nums'>
                                {nextCode}
                            </span>
                        </span>

                        <span className='flex items-center gap-2 text-muted-foreground'>
                            <Clock3 className='size-4 text-primary' />
                            Registration time is saved automatically
                        </span>
                    </div>
                )}

                <div className='grid gap-x-4 gap-y-5 sm:grid-cols-2'>
                    <Field label='Full name' htmlFor='patient-name' required error={errors.name}>
                        <Input
                            id='patient-name'
                            autoFocus
                            autoComplete='off'
                            value={state.name}
                            onChange={event => update('name', event.target.value)}
                            placeholder='e.g. Anjali Kumar'
                            aria-invalid={Boolean(errors.name) || undefined}
                        />
                    </Field>

                    <Field label='Phone number' htmlFor='patient-phone' required error={errors.phone}>
                        <Input
                            id='patient-phone'
                            type='tel'
                            inputMode='tel'
                            autoComplete='off'
                            value={state.phone}
                            onChange={event => update('phone', event.target.value)}
                            placeholder='e.g. 98470 12345'
                            aria-invalid={Boolean(errors.phone) || undefined}
                        />
                    </Field>

                    {matches.length > 0 && (
                        <div
                            role='status'
                            className={cn(
                                'rounded-xl border p-3 sm:col-span-2',
                                phoneMatch ? 'border-amber-300 bg-amber-50' : 'border-border bg-muted/50'
                            )}
                        >
                            <p className='flex items-center gap-2 text-[13px] font-semibold text-foreground'>
                                <CircleAlert className={cn('size-4', phoneMatch ? 'text-amber-600' : 'text-muted-foreground')} />
                                {phoneMatch ? 'This phone number is already registered' : 'A patient with this name already exists'}
                            </p>

                            <p className='mt-0.5 text-xs text-muted-foreground'>
                                {editing
                                    ? 'Check that this is not a duplicate record.'
                                    : 'For returning patients, open the existing record instead of registering again.'}
                            </p>

                            <ul className='mt-2.5 flex flex-col gap-2'>
                                {matches.map(({ patient: match, samePhone }) => (
                                    <li key={match.id} className='flex flex-wrap items-center gap-3 rounded-lg bg-background p-2 ring-1 ring-border'>
                                        <PatientAvatar name={match.name} seed={match.id} size='sm' />

                                        <div className='min-w-0 flex-1'>
                                            <p className='truncate text-sm font-medium text-foreground'>
                                                {match.name}
                                            </p>

                                            <p className='truncate text-xs text-muted-foreground tabular-nums'>
                                                {match.code} · {formatPhone(match.phone)}
                                                {samePhone ? ' · Same phone' : ' · Same name'}
                                            </p>
                                        </div>

                                        {!editing && (
                                            <div className='flex gap-1.5'>
                                                <Button type='button' size='sm' variant='outline' onClick={() => onOpenExisting(match, 'profile')}>
                                                    Open
                                                </Button>

                                                <Button type='button' size='sm' variant='secondary' onClick={() => onOpenExisting(match, 'consult')}>
                                                    <Stethoscope />
                                                    Consult
                                                </Button>
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>

                            {duplicateAcknowledged && (
                                <p className='mt-2.5 text-xs font-medium text-amber-800'>
                                    Family members can share a number. Press save again to register this person as a new patient.
                                </p>
                            )}
                        </div>
                    )}

                    <Field
                        label={state.ageMode === 'age' ? 'Age' : 'Date of birth'}
                        htmlFor={state.ageMode === 'age' ? 'patient-age' : 'patient-dob'}
                        error={state.ageMode === 'age' ? errors.age : errors.dateOfBirth}
                        hint={dobAge !== null ? `Age: ${formatAge(dobAge)}` : undefined}
                        action={(
                            <SegmentedControl
                                options={AGE_MODE_OPTIONS}
                                value={state.ageMode}
                                onChange={value => update('ageMode', value)}
                                ariaLabel='Record age or date of birth'
                                className='h-7 w-auto [&_button]:h-6 [&_button]:px-2 [&_button]:text-xs'
                            />
                        )}
                    >
                        {state.ageMode === 'age' ? (
                            <div className='relative'>
                                <Input
                                    id='patient-age'
                                    type='number'
                                    inputMode='numeric'
                                    min={0}
                                    max={120}
                                    value={state.age}
                                    onChange={event => update('age', event.target.value)}
                                    placeholder='e.g. 34'
                                    className='pr-12'
                                    aria-invalid={Boolean(errors.age) || undefined}
                                />

                                <span className='pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground'>
                                    years
                                </span>
                            </div>
                        ) : (
                            <DateField
                                id='patient-dob'
                                value={state.dateOfBirth}
                                onChange={value => update('dateOfBirth', value)}
                                yearRange={[currentYear - 110, currentYear]}
                                invalid={Boolean(errors.dateOfBirth)}
                            />
                        )}
                    </Field>

                    <Field label='Gender' htmlFor='patient-gender'>
                        <SegmentedControl
                            id='patient-gender'
                            options={GENDER_OPTIONS}
                            value={state.gender}
                            onChange={value => update('gender', value)}
                            ariaLabel='Gender'
                            allowDeselect
                            emptyValue=''
                        />
                    </Field>

                    <Field label='Place / Location' htmlFor='patient-place'>
                        <Input
                            id='patient-place'
                            value={state.place}
                            onChange={event => update('place', event.target.value)}
                            placeholder='e.g. Kakkanad'
                        />
                    </Field>

                    <Field label='Address' htmlFor='patient-address'>
                        <Input
                            id='patient-address'
                            value={state.address}
                            onChange={event => update('address', event.target.value)}
                            placeholder='House name, street, city'
                        />
                    </Field>

                    <Field label='Medical history' htmlFor='patient-history'>
                        <Textarea
                            id='patient-history'
                            rows={2}
                            value={state.medicalHistory}
                            onChange={event => update('medicalHistory', event.target.value)}
                            placeholder='e.g. Diabetes, hypertension, current medicines'
                            className='min-h-16'
                        />

                        <SuggestionChips
                            items={MEDICAL_HISTORY_SUGGESTIONS}
                            onPick={item => update('medicalHistory', appendToText(state.medicalHistory, item))}
                        />
                    </Field>

                    <Field label='Allergies' htmlFor='patient-allergies'>
                        <Textarea
                            id='patient-allergies'
                            rows={2}
                            value={state.allergies}
                            onChange={event => update('allergies', event.target.value)}
                            placeholder='e.g. Penicillin'
                            className='min-h-16'
                        />

                        <SuggestionChips
                            items={ALLERGY_SUGGESTIONS}
                            onPick={item => update('allergies', appendToText(state.allergies, item))}
                        />
                    </Field>

                    <Field label='Additional notes' htmlFor='patient-notes' className='sm:col-span-2'>
                        <Textarea
                            id='patient-notes'
                            rows={2}
                            value={state.notes}
                            onChange={event => update('notes', event.target.value)}
                            placeholder='Anything the clinic should remember about this patient'
                            className='min-h-16'
                        />
                    </Field>
                </div>
            </form>

            <div className='flex flex-col-reverse gap-2 border-t bg-muted/40 px-5 py-3.5 sm:flex-row sm:items-center sm:justify-end'>
                <Button type='button' variant='ghost' onClick={() => onOpenChange(false)}>
                    Cancel
                </Button>

                {editing ? (
                    <Button type='submit' form='patient-form' size='lg'>
                        Save changes
                    </Button>
                ) : (
                    <>
                        <Button type='submit' form='patient-form' variant='outline' size='lg'>
                            Save patient
                        </Button>

                        <Button type='button' size='lg' onClick={() => submit('consult')}>
                            <Stethoscope />
                            Save & start consultation
                        </Button>
                    </>
                )}
            </div>
        </>
    )
}

const PatientFormDialog: FC<PatientFormDialogProps> = ({ open, onOpenChange, ...props }): ReactNode => {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl'>
                <PatientForm onOpenChange={onOpenChange} {...props} />
            </DialogContent>
        </Dialog>
    )
}

export default PatientFormDialog
